import { useState, useEffect } from "react";
import { getApiUrl } from "../hooks/useAuth";
import { ToastContainer, toast } from "react-toastify";
import { formatDateToDDMMYYYY } from "../components/FormatedDate";

export default function AddNewBadgeCycle({ onClose, onSuccess }) {
     const [branches, setBranches] = useState([]);
     const [technicians, setTechnicians] = useState([]);
     const [badges, setBadges] = useState([]);
     const [badgeCycles, setBadgeCycles] = useState([]);
     const [selectedBranch, setSelectedBranch] = useState("");
     const [quarter, setQuarter] = useState("Q1");
     const [loading, setLoading] = useState(false);
     const [statusUpdates, setStatusUpdates] = useState({});
     const year = new Date().getFullYear();
     const token = sessionStorage.getItem("token") || localStorage.getItem("token");
     const quarterToPeriod = {
          Q1: "Jan–Mar",
          Q2: "Apr–Jun",
          Q3: "Jul–Sep",
          Q4: "Oct–Dec",
     };
     const statusOptions = [
          "ISSUED",
          "RETURNED",
          "SENT_TO_VENDOR",
          "ANALYZED"
     ];

     // Fetch branches, technicians, badges on mount
     useEffect(() => {
          const fetchBranches = async () => {
               try {
                    const apiUrl = getApiUrl("/getBranches");
                    const res = await fetch(apiUrl, {
                         headers: { Authorization: `Bearer ${token}` },
                    });
                    const data = await res.json();
                    setBranches(Array.isArray(data.data) ? data.data : []);
               } catch {
                    toast.error("Failed to fetch branches");
               }
          };
          const fetchTechnicians = async () => {
               try {
                    const apiUrl = getApiUrl("/getTechnicians");
                    const res = await fetch(apiUrl, {
                         headers: { Authorization: `Bearer ${token}` },
                    });
                    const data = await res.json();
                    setTechnicians(Array.isArray(data.data) ? data.data : []);
               } catch {
                    toast.error("Failed to fetch technicians");
               }
          };
          const fetchBadges = async () => {
               try {
                    const apiUrl = getApiUrl("/getBadges");
                    const res = await fetch(apiUrl, {
                         headers: { Authorization: `Bearer ${token}` },
                    });
                    const data = await res.json();
                    setBadges(Array.isArray(data.data) ? data.data : []);
               } catch {
                    toast.error("Failed to fetch badges");
               }
          };
          fetchBranches();
          fetchTechnicians();
          fetchBadges();
     }, [token]);

     // Fetch badge cycles for selected branch/year/quarter
     useEffect(() => {
          if (!selectedBranch || !quarter) {
               setBadgeCycles([]);
               return;
          }
          setLoading(true);
          const fetchBadgeCycles = async () => {
               try {
                    const apiUrl = getApiUrl(
                         `/badge_cycles?branch_id=${selectedBranch}&year=${year}&quarter=${quarter}`
                    );
                    const res = await fetch(apiUrl, {
                         headers: { Authorization: `Bearer ${token}` },
                    });
                    const data = await res.json();
                    setBadgeCycles(Array.isArray(data.data) ? data.data : []);
                    // Set initial status updates
                    const statusMap = {};
                    (Array.isArray(data.data) ? data.data : []).forEach(cycle => {
                         statusMap[cycle.cycle_id] = cycle.status;
                    });
                    setStatusUpdates(statusMap);
               } catch {
                    toast.error("Failed to fetch badge cycles");
                    setBadgeCycles([]);
               } finally {
                    setLoading(false);
               }
          };
          fetchBadgeCycles();
     }, [selectedBranch, quarter, year, token]);

     // Filter technicians by selected branch
     const filteredTechnicians = selectedBranch
          ? technicians.filter((t) => String(t.branch_id) === String(selectedBranch))
          : [];

     // For each technician, find their badge (assuming one badge per technician)
     const getBadgeForTechnician = (techni_id) =>
          badges.find((b) => String(b.techni_id) === String(techni_id));

     // For each technician, check if badge_cycle exists for this quarter
     const getCycleForTechnician = (techni_id) =>
          badgeCycles.find((c) => String(c.techni_id) === String(techni_id));

     // Prepare new cycles to create
     const newCycles = filteredTechnicians
          .filter((tech) => !getCycleForTechnician(tech.techni_id))
          .map((tech) => {
               const badge = getBadgeForTechnician(tech.techni_id);
               return {
                    badge_id: badge ? badge.badge_id : "",
                    badge_code: badge ? badge.badge_code : "",
                    techni_id: tech.techni_id,
                    tech_name: tech.name,
               };
          });

     // Handle create new cycles
     const handleCreateCycles = async (e) => {
          e.preventDefault();
          if (newCycles.length === 0) {
               toast.info("All technicians already have cycles for this quarter.");
               return;
          }
          // Validate all badge_id present
          if (newCycles.some((c) => !c.badge_id)) {
               toast.error("Some technicians do not have a badge assigned.");
               return;
          }
          try {
               setLoading(true);
               const apiUrl = getApiUrl("/badge_cycles/bulk");
               const payload = {
                    cycles: newCycles.map((c) => ({
                         badge_id: c.badge_id,
                         techni_id: c.techni_id,
                         branch_id: selectedBranch,
                         year,
                         quarter,
                         cycle_period: quarterToPeriod[quarter],
                         status: "ISSUED",
                    })),
               };
               const res = await fetch(apiUrl, {
                    method: "POST",
                    headers: {
                         "Content-Type": "application/json",
                         Authorization: `Bearer ${token}`,
                    },
                    body: JSON.stringify(payload),
               });
               if (!res.ok) throw new Error("Failed to add badge cycles");
               toast.success("Badge cycles created!");
               if (onSuccess) onSuccess();
               if (onClose) onClose();
               window.location.href = "/badge-cycles";
          } catch {
               toast.error("Failed to add badge cycles");
          } finally {
               setLoading(false);
          }
     };

     // Handle status update for existing cycles
     const handleStatusChange = (cycle_id, newStatus) => {
          setStatusUpdates((prev) => ({
               ...prev,
               [cycle_id]: newStatus,
          }));
     };

     const handleUpdateStatuses = async (e) => {
          e.preventDefault();
          const updates = badgeCycles
               .filter((cycle) => statusUpdates[cycle.cycle_id] && statusUpdates[cycle.cycle_id] !== cycle.status)
               .map((cycle) => ({
                    cycle_id: cycle.cycle_id,
                    status: statusUpdates[cycle.cycle_id],
               }));
          if (updates.length === 0) {
               toast.info("No status changes to update.");
               return;
          }
          try {
               setLoading(true);
               const apiUrl = getApiUrl("/badge_cycles/update-status-bulk");
               const res = await fetch(apiUrl, {
                    method: "POST",
                    headers: {
                         "Content-Type": "application/json",
                         Authorization: `Bearer ${token}`,
                    },
                    body: JSON.stringify({ updates }),
               });
               if (!res.ok) throw new Error("Failed to update statuses");
               toast.success("Statuses updated!");

          } catch {
               toast.error("Failed to update statuses");
          } finally {
               setLoading(false);
          }
     };

     const quarters = [
          { value: "Q1", label: "Jan–Mar" },
          { value: "Q2", label: "Apr–Jun" },
          { value: "Q3", label: "Jul–Sep" },
          { value: "Q4", label: "Oct–Dec" },
     ];

     return (
          <div style={{ padding: "5px" }}>
               <ToastContainer position="top-right" />
               <div className="d-flex justify-content-between align-items-center mb-4">
                    <h2 className="h4 mb-4">Add / Edit Badge Cycles (By selecting Branch)</h2>
                    {/* redirect to badge cycles page */}
                    <button className="btn btn-secondary" onClick={() => window.location.href = "/badge-cycles"}>
                         Back to Badge Cycles
                    </button>

               </div>


               <form onSubmit={newCycles.length > 0 ? handleCreateCycles : handleUpdateStatuses} className="border p-4 rounded bg-light">
                    <div className="form-row">
                         <div className="form-group col-md-4">
                              <label>Branch</label>
                              <select
                                   className="form-control"
                                   value={selectedBranch}
                                   onChange={(e) => setSelectedBranch(e.target.value)}
                                   required
                              >
                                   <option value="">Select Branch</option>
                                   {branches.map((b) => (
                                        <option key={b.branch_id} value={b.branch_id}>
                                             {b.branch_name}
                                        </option>
                                   ))}
                              </select>
                         </div>
                         <div className="form-group col-md-2">
                              <label>Year</label>
                              <input
                                   type="number"
                                   className="form-control"
                                   value={year}
                                   readOnly
                              />
                         </div>
                         <div className="form-group col-md-2">
                              <label>Quarter</label>
                              <select
                                   className="form-control"
                                   value={quarter}
                                   onChange={(e) => setQuarter(e.target.value)}
                                   required
                              >
                                   {quarters.map((q) => (
                                        <option key={q.value} value={q.value}>
                                             {q.label}
                                        </option>
                                   ))}
                              </select>
                         </div>
                    </div>

                    {selectedBranch && (
                         <div className="mt-4">
                              <h5>Technicians in Branch</h5>
                              {loading ? (
                                   <div>Loading...</div>
                              ) : (
                                   <table className="table table-bordered">
                                        <thead>
                                             <tr>
                                                  <th>Technician</th>
                                                  <th>Badge Code</th>
                                                  <th>Status</th>
                                                  <th colSpan={4}> </th>
                                             </tr>
                                        </thead>
                                        <tbody>
                                             {badgeCycles.length > 0 ? (
                                                  badgeCycles.map((cycle) => (
                                                       <tr key={cycle.cycle_id}>
                                                            <td>{cycle.technician_name}</td>
                                                            <td>{cycle.badge_code}</td>
                                                            <td>
                                                                 <select
                                                                      className="form-control"
                                                                      value={statusUpdates[cycle.cycle_id] || cycle.status}
                                                                      onChange={(e) => handleStatusChange(cycle.cycle_id, e.target.value)}
                                                                 >
                                                                      {statusOptions.map((s) => (
                                                                           <option key={s} value={s}>{s}</option>
                                                                      ))}
                                                                 </select>
                                                            </td>
                                                            <td>
                                                                 Issued by : {cycle.issued_by}<br />
                                                                 On : {cycle.issued_at
                                                                      ? formatDateToDDMMYYYY(cycle.issued_at)
                                                                      : "--"}
                                                            </td>
                                                            {cycle.returned_at ? (
                                                                 <td>
                                                                      Returned by : {cycle.returned_by}<br />
                                                                      On : {cycle.returned_at
                                                                           ? formatDateToDDMMYYYY(cycle.returned_at)
                                                                           : "--"}
                                                                 </td>
                                                            ) : ''}

                                                            {cycle.received_at ? (
                                                                 <td>
                                                                      Received by : {cycle.received_by}<br />
                                                                      On : {cycle.received_at
                                                                           ? formatDateToDDMMYYYY(cycle.received_at)
                                                                           : "--"}
                                                                 </td>
                                                            ) : ('')}
                                                            {cycle.processed_date ? (
                                                                 <td>
                                                                      Processed by : {cycle.processed_by}<br />
                                                                      On : {cycle.processed_date
                                                                           ? formatDateToDDMMYYYY(cycle.processed_date)
                                                                           : "--"}
                                                                 </td>
                                                            ) : ('')}
                                                       </tr>
                                                  ))
                                             ) : (
                                                  <tr>
                                                       <td colSpan={8}>No technicians found for this branch.</td>
                                                  </tr>
                                             )}
                                        </tbody>
                                   </table>
                              )}
                         </div>
                    )}

                    {selectedBranch && newCycles.length > 0 && (
                         <div className="mt-3">
                              <button type="submit" className="btn btn-primary" disabled={loading}>
                                   {loading ? "Creating..." : "Create New Cycles"}
                              </button>
                         </div>
                    )}
                    {selectedBranch && newCycles.length === 0 && badgeCycles.length > 0 && (
                         <div className="mt-3">
                              <button type="submit" className="btn btn-success" disabled={loading}>
                                   {loading ? "Updating..." : "Update Status"}
                              </button>
                         </div>
                    )}
                    {onClose && (
                         <div className="mt-3">
                              <button type="button" className="btn btn-secondary" onClick={onClose}>
                                   Cancel
                              </button>
                         </div>
                    )}
               </form>
          </div>
     );
}